import { useSelector } from 'react-redux';
import { generatePath, Link, NavLink } from 'react-router-dom';
import { Translate, useTranslate } from 'react-translate.ts';
import { NavLogo } from '../';
import { NavLanguage } from './NavLanguage';
import SignOutButton from './SignOutButton';
import {
    ADMIN_COMPANIES_PATH,
    ADMIN_COMPANY_PATH,
    ADMIN_DASHBOARD_PATH,
    ADMIN_PATH,
    OUTPUT_REPORT_PATH,
    PARTICIPANT_PATH,
    PASSWORD_PATH,
    PROFILE_PATH,
} from '../../constants/route.constants';
import { useSwitch } from '../../hooks/useSwitch';
import { useDropdown } from '../../hooks/useDropdown';
import NavMenuMobile from './NavMenuMobile/NavMenuMobile';
import AuthSelectors from '../../selectors/AuthSelectors';
import ProfileSelectors from '../../selectors/ProfileSelectors';
import ContactModal from './ContactModal/ContactModal';
import useContactModal from './useContactModal';
import ContactButton from './ContactButton';
import AdminService from '../../common/services/AdminService';
import { AuthService } from '../../services/AuthService';
import { UserService } from '../../services/common/UserService';
import styles from './Navbar.module.scss';
import { useMemo } from 'react';

export function Navbar({ isUserLogged }) {
    const authToken = useSelector(AuthSelectors.getAuthToken);
    const isAdmin =
        AdminService.isAdminAllowed() && AuthService.isUserAdmin(authToken);
    const companyId = useMemo(
        () => AuthService.getCompanyId(authToken),
        [authToken],
    );
    const companyName = useMemo(
        () => AuthService.getCompanyName(authToken),
        [authToken],
    );

    return (
        <nav className={styles.navbar} role="navigation">
            <NavLogo />
            <div className={styles.navigation}>
                <Links
                    isUserLogged={isUserLogged}
                    isAdmin={isAdmin}
                    companyId={companyId}
                    companyName={companyName}
                />
                <NavLanguage />
            </div>
            <NavMenuMobile
                isUserLogged={isUserLogged}
                isAdmin={isAdmin}
                companyId={companyId}
                companyName={companyName}
            />
        </nav>
    );
}

function Links(props: {
    isUserLogged: boolean;
    isAdmin: boolean;
    companyId: number | null;
    companyName: string | null;
}) {
    const { isAdmin, isUserLogged, companyId, companyName } = props;
    const contactModalState = useContactModal();
    const isAnonymous = useSelector(AuthSelectors.isAnonymous);

    return (
        <>
            {isUserLogged && (
                <>
                    <NavLink to={PARTICIPANT_PATH} className={styles.item}>
                        <Translate>fe.nav.todo</Translate>
                    </NavLink>
                    {!isAnonymous && (
                        <NavLink
                            to={OUTPUT_REPORT_PATH}
                            className={styles.item}
                        >
                            <Translate>fe.nav.results</Translate>
                        </NavLink>
                    )}
                    {isAdmin && (
                        <NavLink
                            to={`${ADMIN_PATH}/${ADMIN_DASHBOARD_PATH}`}
                            className={styles.item}
                        >
                            <Translate>fe.nav.admin</Translate>
                        </NavLink>
                    )}
                    <Profile
                        isAdmin={isAdmin}
                        companyId={companyId}
                        companyName={companyName}
                    />
                </>
            )}
            <div className={styles.item} onClick={contactModalState.toggle}>
                <ContactButton />
            </div>
            <ContactModal
                isLogged={isUserLogged}
                isVisible={contactModalState.isVisible}
                onClose={contactModalState.close}
            />
        </>
    );
}

function Profile(props: {
    companyId: number | null;
    isAdmin: boolean;
    companyName: string | null;
}) {
    const { companyId, isAdmin, companyName } = props;
    const [isOpen, , close, toggle] = useSwitch();
    const dropdownRef = useDropdown(isOpen, close);
    const translate = useTranslate();

    const profileName = useSelector(ProfileSelectors.getProfileName);
    const isAnonymous = useSelector(AuthSelectors.isAnonymous);

    return (
        <div className={styles.item}>
            <div data-test="profile-nav-link">
                <div onClick={toggle}>
                    {createName(profileName, isAnonymous)}
                </div>
                {renderDropdown(isAdmin, companyId)}
            </div>
        </div>
    );

    function createName(profileName: string, isAnonymous: boolean) {
        if (isAnonymous) {
            return translate('fe.nav.user.anonymous');
        }

        if (profileName.length > 0) {
            return profileName;
        }

        return translate('fe.nav.user.profile');
    }

    function renderDropdown(isAdmin: boolean, companyId: number | null) {
        if (!isOpen) {
            return null;
        }

        return (
            <div className={styles.item__menu} ref={dropdownRef as any}>
                <ul>
                    {isAnonymous !== true && (
                        <li>
                            <Link
                                to={PROFILE_PATH}
                                className={styles.item__menu__item}
                            >
                                {translate('profile')}
                            </Link>
                        </li>
                    )}
                    {isAdmin && companyId && (
                        <li>
                            <Link
                                to={generatePath(
                                    `${ADMIN_PATH}/${ADMIN_COMPANIES_PATH}/${ADMIN_COMPANY_PATH}`,
                                    {
                                        companyId: companyId.toString(),
                                    },
                                )}
                                className={styles.item__menu__item}
                            >
                                {companyName || translate('spolecnost')}
                                {/* TODO: translate */}
                            </Link>
                        </li>
                    )}
                    {UserService.isAnonymous() !== true && (
                        <li>
                            <div className={styles.item__menu__item}>
                                <Link to={PASSWORD_PATH} onClick={close}>
                                    {translate('change-password')}
                                </Link>
                            </div>
                        </li>
                    )}
                    <li>
                        <SignOutButton onClick={close} />
                    </li>
                </ul>
            </div>
        );
    }
}
